import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { Company } from 'situview-webapi-client';
import { AuthService, BreadcrumbService, CompanyViewService, StateService } from 'src/app/services';
import { Router } from '@angular/router';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { CompanySummary, CompanyType, IdentityWebApiClient, ListCompaniesRequest, ListCompaniesResponse } from 'identity-webapi-client';
import { concatMap, takeWhile } from 'rxjs/operators';
import { interval, of } from 'rxjs';

@Component({
  selector: 'companies-list',
  templateUrl: './companies-list.component.html',
  styleUrls: ['./companies-list.component.scss']
})

export class CompaniesListComponent extends SituBaseComponent implements AfterViewInit {
  enabledOnly: boolean = true;
  previousSearchTerm = "";
  searchTerm = "";
  companies: CompanySummary[] = [];
  filteredCompanies: CompanySummary[] = [];
  companyTypes = CompanyType;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private companyViewService: CompanyViewService,
    private identityWebApiClient: IdentityWebApiClient,
    private router: Router,
    private breadcrumbService: BreadcrumbService) {
      super(authService, stateService);
      this.loadIdentityCompanies();
      this.loadCompanies();
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  /* auto paging */
  pageSize = 8;
  pageLength = 8;
  @ViewChild('showMoreMarker') showMoreMarker: ElementRef | undefined;
  private observer: IntersectionObserver | undefined;
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMoreProductsToShow()) {
          this.showMoreProducts();
        }
      });
    });

    if (this.showMoreMarker?.nativeElement) {
      this.observer.observe(this.showMoreMarker.nativeElement);
    }
  }

  showMoreProducts() {
    const incrementSize = 2;
    const intervalTime = 200;
    const targetPageLength = this.pageLength + this.pageSize;
    this._subscriptions.push(interval(intervalTime).pipe(
      takeWhile(() => this.pageLength < targetPageLength && this.pageLength < this.filteredCompanies.length),
      concatMap(() => {
        const nextPageLength = Math.min(this.pageLength + incrementSize, targetPageLength, this.filteredCompanies.length);
        return of(nextPageLength);
      })
    ).subscribe((nextPageLength) => {
      this.pageLength = nextPageLength;
    }));
  }

  hasMoreProductsToShow(): boolean {
    return this.filteredCompanies.length > this.pageLength;
  }
  /* auto paging */

  onClickAddCompany() {
    this.router.navigate([`/admin/companies/add`]);
  }

  onEditManufacturer(companyId: string) {
    this.router.navigate([`/admin/companies/${companyId}`]);
  }

  onViewCompany(manufacturerId: string) {
    this.router.navigate([`/admin/companies/${manufacturerId}/dashboard`]);
  }

  getCompanyLogo(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.logoUrl;
  }

  getCompanyName(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.name;
  }

  filterData() {
    this.pageLength = this.pageSize;
    this.filteredCompanies = this.companies.filter((item: CompanySummary) => {
      return !this.searchTerm || item.name?.toLowerCase()?.includes(this.searchTerm.toLowerCase())
    });
  }

  private loadIdentityCompanies() {
    this.loading = true;

    let request = new ListCompaniesRequest();
    request.pageNumber = 1;
    request.pageSize = 300;
    request.includeUserCount = true;

    return this.identityWebApiClient.listCompanies(request)
      .then((response: ListCompaniesResponse) => {
        this.companies = response.items ?? [];
        this.filterData();
      })
      .catch((err: any) => {

      })
      .finally(() => {
        this.loading = false;
      });
  }

  allCompanies: Company[] = [];
  private loadCompanies() {
    return this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
      });
  }
}